import React, { useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import PixelRunner from "./motion/PixelRunner";
import { markChooserSeen } from "../lib/chooserSession";

type ModeChooserProps = {
  /** Open only once the page loader has hidden. */
  ready: boolean;
  onClose: () => void;
};

const FOCUSABLE = "button, [href], [tabindex]:not([tabindex='-1'])";

const ModeChooser: React.FC<ModeChooserProps> = ({ ready, onClose }) => {
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement>(null);
  const playRef = useRef<HTMLButtonElement>(null);
  const browseRef = useRef<HTMLButtonElement>(null);

  // Mark as seen the moment it opens, whatever the visitor picks next.
  useEffect(() => {
    if (!ready) return;
    markChooserSeen();

    const reduceMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    (reduceMotion ? browseRef : playRef).current?.focus();
  }, [ready]);

  if (!ready) return null;

  const handlePlay = () => {
    onClose();
    navigate({ to: "/play" });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const items = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (!items || items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mode-chooser-title"
        className="w-full max-w-2xl rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl text-slate-100"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-10 h-12 shrink-0">
            <PixelRunner state="idle" />
          </div>
          <h2 id="mode-chooser-title" className="text-xl sm:text-2xl font-semibold m-0">
            How do you want to explore?
          </h2>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <button
            ref={playRef}
            type="button"
            onClick={handlePlay}
            className="text-left rounded-xl border border-cyan-500/60 bg-slate-800 p-5 hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-400"
          >
            <span className="block text-lg font-semibold text-cyan-300">
              🎮 Play the game
            </span>
            <span className="block mt-1 text-sm text-slate-300">
              Run through my career Mario-style · ~3 min · keyboard or touch
            </span>
          </button>

          <button
            ref={browseRef}
            type="button"
            onClick={onClose}
            className="text-left rounded-xl border border-slate-600 bg-slate-800 p-5 hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-400"
          >
            <span className="block text-lg font-semibold text-slate-100">
              📄 Browse portfolio
            </span>
            <span className="block mt-1 text-sm text-slate-300">
              The classic site
            </span>
          </button>
        </div>

        <p className="mt-4 mb-0 text-xs text-slate-400">
          You can jump into the game anytime from the top bar.
        </p>
      </div>
    </div>
  );
};

export default ModeChooser;
